import Badge from "@/common/components/Badge";
import { CurrencyTextField } from "@/common/components/CurrencyTextField";
import { UnitTextField } from "@/common/components/UnitTextField";
import { unities, unitiesDecimals } from "@/common/constants/unities.constant";
import { Option } from "@/common/types/option";
import { executeAtEndOfCallStack } from "@/common/utils/executeAtEndOfCallStack";
import { formatCurrency, formatDecimalUnit } from "@/common/utils/formatters";
import { getNestedUnitForQuantity } from "@/common/utils/getNestedUnitForQuantity";
import { unFormatCurrency, unFormatUnknownUnit } from "@/common/utils/unFormatters";
import { useGovernmentAllocationsAllowedCategoriesAndProducts } from "@/features/privateCompanies/hooks/useGovernmentAllocationsAllowedCategoriesAndProducts";
import { ProductCategoryDistributionLimitControlledByCurrencyValidationSchema, ProductCategoryDistributionLimitControlledByQuantityValidationSchema, ProductCategoryDistributionLimitValidationSchema } from "@/features/privateCompanies/schemas/governmentAllocations/distributionsLimits.schema";
import { GovernmentAllocationsAllowedCategoriesAndProductsTransformer } from "@/features/privateCompanies/services/governmentAllocationAllowedCategoriesAndProductsTransformer";
import { ControlledByType, GovernmentAllocationAllowedCategoriesAndProducts, GovernmentAllocationAllowedCategoryAndProductData } from "@/features/privateCompanies/types/companyGovernmentAllocation.type";
import { BoxesIcon } from "lucide-react";
import { useId, useMemo } from "react";
import { FieldErrors, useFieldArray, useFormContext, useWatch } from "react-hook-form";
import Select from "@/common/components/Select";
import ProductDistributionLimitFormItem from "./ProductDistributionLimitFormItem";

interface ProductCategoryDistributionLimitFormProps {
  governmentAllocationId?: string;
  productCategoryId?: string;
  controlledBy?: ControlledByType;
}

const ProductCategoryDistributionLimitForm = ({
  governmentAllocationId,
  productCategoryId,
  controlledBy,
}: ProductCategoryDistributionLimitFormProps) => {
  const uniqueId = useId();

  const isQuantity = controlledBy === 'quantity';

  const {
    data: allowedCategoriesAndProducts,
    isFetching: isAllowedCategoriesAndProductsFetching,
    isPending: isAllowedCategoriesAndProductsPending
  } = useGovernmentAllocationsAllowedCategoriesAndProducts<GovernmentAllocationAllowedCategoriesAndProducts>({
    governmentAllocationId,
    filters: { productCategoryId },
  });

  const allowedCategory = useMemo(() => {
    return isAllowedCategoriesAndProductsPending || !productCategoryId
      ? undefined
      : allowedCategoriesAndProducts['categories'][productCategoryId]
  }, [productCategoryId, isAllowedCategoriesAndProductsPending]);

  const allowedProductsOptions = useMemo(() => {
    return isAllowedCategoriesAndProductsPending
      ? []
      : GovernmentAllocationsAllowedCategoriesAndProductsTransformer.optionsTransformer(
        'products', controlledBy, true
      )(allowedCategoriesAndProducts || {})
  }, [isAllowedCategoriesAndProductsPending]);

  const {
    register,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<ProductCategoryDistributionLimitValidationSchema>();

  const {
    fields: productsDistributionLimits,
    append: appendProductDistributionLimit,
    remove: removeProductDistributionLimt,
  } = useFieldArray({
    control,
    name: 'product_distribution_limits_attributes',
  });

  const currentValue = useWatch({
    control,
    name: isQuantity
      ? `maximum_quantity`
      : `maximum_value`,
  });

  const productCategoryUnit = useMemo(() => getNestedUnitForQuantity(
    productsDistributionLimits || [],
    (p) => p.product?.unit_for_quantity,
  ), [(productsDistributionLimits || []).length]);

  const setProductCategoryTotalValue = () => {
    executeAtEndOfCallStack(() => {
      if (isQuantity) {
        const productCategoriesTotalQuantitySum = productsDistributionLimits.reduce((sum, _, index) => {
          const productTotalQuantity = watch(`product_distribution_limits_attributes.${index}.maximum_quantity`);

          return sum + (productTotalQuantity ? unFormatUnknownUnit(productTotalQuantity) : 0);
        }, 0);

        setValue(`maximum_quantity`, formatDecimalUnit(productCategoriesTotalQuantitySum, productCategoryUnit));
      } else {
        const productCategoriesTotalValueSum = productsDistributionLimits.reduce((sum, _, index) => {
          const productTotalValue = watch(`product_distribution_limits_attributes.${index}.maximum_value`);

          return sum + (productTotalValue ? unFormatCurrency(productTotalValue) : 0);
        }, 0);

        setValue(`maximum_value`, formatCurrency(productCategoriesTotalValueSum));
      }
    });
  };


  return (
    <div className="flex flex-col gap-6 text-white-800">
      <Badge
        Icon={BoxesIcon}
        iconProps={{ className: 'size-6', strokeWidth: 1 }}
        text={allowedCategory?.display_name || ''}
        className="rounded-sm flex bg-neutral-op-8 w-fit flex-row-reverse overflow-hidden !p-0 pe-2 gap-0"
        overwriteClassName
        contentClassName="px-3 py-2"
      />

      <div className="max-h-[480px] min-h-[320px] overflow-auto flex flex-col gap-6 pe-2">
        <div className="flex flex-col gap-1">
          {isQuantity ? (
            <UnitTextField
              label="Quantidade máxima"
              decimals={productCategoryUnit ? unitiesDecimals[productCategoryUnit] : 2}
              unit={productCategoryUnit ? unities[productCategoryUnit] : undefined}
              {...register('maximum_quantity')}
              forceValue
              value={currentValue}
              errorMessage={
                (errors as FieldErrors<ProductCategoryDistributionLimitControlledByQuantityValidationSchema>)
                  .maximum_quantity
                  ?.message || undefined
              }
              disabled={productsDistributionLimits?.length > 0}
              max={999_999_999_999.99}
            />
          ) : (
            <CurrencyTextField
              label="Valor máximo"
              {...register('maximum_value')}
              forceValue
              value={currentValue}
              errorMessage={
                (errors as FieldErrors<ProductCategoryDistributionLimitControlledByCurrencyValidationSchema>)
                  .maximum_value
                  ?.message || undefined
              }
              disabled={productsDistributionLimits?.length > 0}
              max={999_999_999_999.99}
            />
          )}
        </div>

        <Select
          placeholder="Selecione um produto"
          isLoading={isAllowedCategoriesAndProductsFetching}
          options={allowedProductsOptions?.filter((productOption) => {
            return !productsDistributionLimits?.some(({ product }) => product?.id === productOption.value);
          }) || []}
          value=""
          onChange={(newValue) => {
            appendProductDistributionLimit({
              product: {
                id: (newValue as Option).value,
                display_name: (newValue as GovernmentAllocationAllowedCategoryAndProductData).display_name,
                unit_for_quantity: (newValue as GovernmentAllocationAllowedCategoryAndProductData).unit_for_quantity,
                category: {
                  id: '',
                  display_name: '',
                  products_count: '',
                }
              },
              maximum_value: '',
              maximum_quantity: '',
            });

            setProductCategoryTotalValue();
          }}
        />

        {productsDistributionLimits?.map((productDistributionLimit, index) => (
          <ProductDistributionLimitFormItem
            key={`${uniqueId}-${productDistributionLimit?.product?.id}`}
            index={index}
            productDistributionLimit={productDistributionLimit}
            onDelete={() => {
              removeProductDistributionLimt(index);
              setProductCategoryTotalValue();
            }}
            onTotalValueChange={setProductCategoryTotalValue}
            controlledBy={controlledBy}
          />
        ))}
      </div>
    </div>
  );
}

export default ProductCategoryDistributionLimitForm;
